/*
 * /rest-hours — учёт часов работы и отдыха моряка (ПДНВ / КТМС).
 *
 * Сетка: строка — сутки, колонка — полчаса. На широком экране закрашивается
 * протягиванием, как бумажный бланк; на узком строка суток открывает диалог с
 * крупной сеткой на 12 часов в строке — ячейка в 5 px пальцем не попадается.
 *
 * Что изменилось 2026-09-13 и почему:
 *  · МЕСЯЦЫ ХРАНЯТСЯ ОТДЕЛЬНО. Раньше смена месяца перезаписывала единственную
 *    запись `sheet` — прошлый месяц пропадал молча. Теперь ключ на моряка и
 *    месяц, и первые шесть суток проверяются вместе с концом прошлого месяца
 *    (недельная норма через границу месяца раньше не проверялась вообще).
 *  · СУТКИ 23/25 ЧАСОВ. Перевод судовых часов — обычное дело на переходе, а
 *    конвенции считают реальное время.
 *  · PDF НА ПОДПИСЬ. JSON — вход для программы, а не отчёт; человеку нужна
 *    форма в духе таблицы IMO/ILO с местом для двух подписей. JSON остался как
 *    резервная копия, и его теперь можно ЗАГРУЗИТЬ обратно.
 *  · ЭКИПАЖ (Премиум). Старпому нужны формы на всех, а не на себя.
 */

import { evaluate, applyPattern, copyDay, dayWorkSpans, dayStarts, workBudget,
    SLOTS_PER_DAY, SLOT_H, RULES, hhmm, fmtH } from './engine.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__RH || { T: {}, cfg: {} };
const t = makeT(B.T);
const LOC = B.cfg.locale || 'ru';
const SLUG = 'rest-hours';
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
const isPro = () => !!(window.__isProNow && window.__isProNow(false));
const store = {
    load: (k, d) => { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch { return d; } },
    save: (k, v) => { try { window.__toolSettings?.save(SLUG, k, v); } catch { /* приватный режим */ } },
};
const funnel = (ev, d) => { try { window.__aeFunnel?.(ev, d || {}); } catch { } };

/* ── состояние ───────────────────────────────────────────────────────────── */
const S = {
    crew: [],            // [{id, name, rank, watch}]
    who: null,           // id
    ship: { name: '', flag: '', imo: '' },
    ym: '',              // YYYY-MM
    days: 30,
    clock: {},           // day -> delta получасов (−2…+2)
    flat: [],            // реальное время, получасы подряд
    ctx: null,           // {flat, lens, days} — хвост прошлого месяца
    result: null,
    exc: false, breaks: false,
    paint: null,
    editDay: 0,
};

const ymOf = d => d.toISOString().slice(0, 7);
const daysIn = ym => { const [y, m] = ym.split('-').map(Number); return new Date(Date.UTC(y, m, 0)).getUTCDate(); };
const dateOf = (ym, i) => { const d = new Date(ym + '-01T00:00:00Z'); d.setUTCDate(1 + i); return d; };
const shiftYm = (ym, k) => { const d = new Date(ym + '-01T00:00:00Z'); d.setUTCMonth(d.getUTCMonth() + k); return ymOf(d); };
const lensOf = (days, clock) => Array.from({ length: days }, (_, d) => SLOTS_PER_DAY + (clock[d] || 0));
const lens = () => lensOf(S.days, S.clock);

/* ── упаковка ───────────────────────────────────────────────────────────── */
const A64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function packBits(flat) {
    let s = '';
    for (let i = 0; i < flat.length; i += 6) {
        let n = 0;
        for (let b = 0; b < 6; b++) if (flat[i + b]) n |= (1 << b);
        s += A64[n];
    }
    return s;
}
function unpackBits(s, len) {
    const out = new Array(len).fill(false);
    for (let i = 0; i < (s || '').length; i++) {
        const n = A64.indexOf(s[i]);
        if (n < 0) continue;
        for (let b = 0; b < 6; b++) { const k = i * 6 + b; if (k < len) out[k] = !!(n & (1 << b)); }
    }
    return out;
}

/* ── хранение: экипаж, судно, месяцы ─────────────────────────────────────── */
const monthKey = (who, ym) => `m:${who}:${ym}`;

function loadMonth(who, ym) {
    const days = daysIn(ym);
    const v = store.load(monthKey(who, ym), null);
    const clock = (v && v.clock) || {};
    const total = lensOf(days, clock).reduce((a, b) => a + b, 0);
    return { days, clock, flat: v ? unpackBits(v.flat, total) : null };
}

function saveMonth() {
    store.save(monthKey(S.who, S.ym), { flat: packBits(S.flat), clock: S.clock, at: Date.now() });
}

function saveMeta() {
    store.save('crew', S.crew);
    store.save('ship', S.ship);
    store.save('who', S.who);
    store.save('opts', { exc: S.exc, breaks: S.breaks });
}

/** Перенос записи v1 (`sheet`, один месяц на всё) в новую схему — один раз. */
function migrate() {
    if (store.load('crew', null)) return;
    const id = 'p1';
    const old = store.load('sheet', null);
    S.crew = [{ id, name: old?.name || '', rank: old?.rank || '', watch: true }];
    S.ship = { name: old?.ship || '', flag: '', imo: '' };
    S.who = id;
    if (old && old.flat && old.start) {
        const ym = String(old.start).slice(0, 7);
        store.save(monthKey(id, ym), { flat: old.flat, clock: {}, at: Date.now() });
    }
    saveMeta();
}

function openMonth(ym) {
    S.ym = ym;
    const m = loadMonth(S.who, ym);
    S.days = m.days;
    S.clock = m.clock;
    if (m.flat) S.flat = m.flat;
    else {
        S.flat = new Array(lens().reduce((a, b) => a + b, 0)).fill(false);
        const prev = loadMonth(S.who, shiftYm(ym, -1));
        /* Первый визит в месяц: продолжаем вахту прошлого месяца, если она
           была, а не рисуем пустоту. */
        if (!prev.flat) applyPattern(S.flat, 'watch-4-8', 0, S.days - 1, lens());
    }
    const prev = loadMonth(S.who, shiftYm(ym, -1));
    if (prev.flat) {
        const plens = lensOf(prev.days, prev.clock);
        const take = Math.min(6, prev.days);
        const st = dayStarts(plens);
        S.ctx = { flat: prev.flat.slice(st[prev.days - take]), lens: plens.slice(prev.days - take), days: take };
    } else S.ctx = null;
    $('rhMonth').value = ym;
    $('rhTo').value = String(S.days);
    ['rhFrom', 'rhTo', 'rhSrc'].forEach(id => $(id).max = String(S.days));
    buildGrid(); recalc();
}

/* ── сетка ───────────────────────────────────────────────────────────────── */
const COLS = 50;       // 48 получасов + 2 под «лишний час» 25-часовых суток

function buildGrid() {
    const wrap = $('rhGrid');
    wrap.textContent = '';
    const head = el('div', 'rh-row rh-head');
    head.append(el('span', 'rh-dc', t('col_day')));
    const scale = el('div', 'rh-scale');
    for (let h = 0; h < 24; h += 2) scale.append(el('span', 'rh-hr', String(h).padStart(2, '0')));
    scale.append(el('span', 'rh-hr plus', '+1'));
    head.append(scale, el('span', 'rh-sum', t('col_rest')));
    wrap.append(head);

    const L = lens(), st = dayStarts(L);
    for (let d = 0; d < S.days; d++) {
        const dt = dateOf(S.ym, d);
        const row = el('div', 'rh-row' + ([0, 6].includes(dt.getUTCDay()) ? ' we' : '') + (S.clock[d] ? ' clk' : ''));
        row.dataset.day = String(d);
        row.setAttribute('role', 'row');
        const dc = el('button', 'rh-dc');
        dc.type = 'button';
        dc.dataset.day = String(d);
        dc.setAttribute('aria-label', t('day_title', { date: dt.toLocaleDateString(LOC, { day: 'numeric', month: 'long', timeZone: 'UTC' }) }));
        dc.append(el('span', 'rh-dn', String(dt.getUTCDate())),
            el('span', 'rh-dw', dt.toLocaleDateString(LOC, { weekday: 'short', timeZone: 'UTC' })));
        row.append(dc);
        const cells = el('div', 'rh-cells');
        for (let s = 0; s < COLS; s++) {
            const active = s < L[d];
            const c = el('span', 'rh-c' + (s % 2 === 0 ? ' h' : '') + (s % 8 === 7 ? ' q' : '') + (active ? '' : ' x'));
            if (active) c.dataset.i = String(st[d] + s);
            cells.append(c);
        }
        row.append(cells, el('span', 'rh-sum'));
        wrap.append(row);
    }
    paintAll();
}

function wireGrid() {
    const wrap = $('rhGrid');
    const idxAt = (x, y) => {
        const e2 = document.elementFromPoint(x, y);
        return e2 && e2.classList.contains('rh-c') && e2.dataset.i ? Number(e2.dataset.i) : null;
    };
    const narrow = () => wrap.getBoundingClientRect().width <= 560;
    wrap.addEventListener('pointerdown', e => {
        const dc = e.target.closest('.rh-dc');
        const row = e.target.closest('.rh-row[data-day]');
        if (dc || (row && narrow())) { openDay(Number((dc || row).dataset.day)); return; }
        const c = e.target.closest('.rh-c');
        if (!c || !c.dataset.i) return;
        e.preventDefault();
        const i = Number(c.dataset.i);
        S.paint = { value: !S.flat[i] };
        setSlot(i, S.paint.value);
        wrap.setPointerCapture?.(e.pointerId);
    });
    wrap.addEventListener('pointermove', e => {
        if (!S.paint) return;
        const i = idxAt(e.clientX, e.clientY);
        if (i !== null) setSlot(i, S.paint.value);
    });
    const stop = () => { if (S.paint) { S.paint = null; recalc(); saveMonth(); } };
    wrap.addEventListener('pointerup', stop);
    wrap.addEventListener('pointercancel', stop);
    window.addEventListener('pointerup', stop);
}

function setSlot(i, v) {
    if (S.flat[i] === v) return;
    S.flat[i] = v;
    const c = $('rhGrid').querySelector(`.rh-c[data-i="${i}"]`);
    if (c) c.classList.toggle('w', v);
}
function paintAll() {
    for (const c of $('rhGrid').querySelectorAll('.rh-c[data-i]')) c.classList.toggle('w', !!S.flat[Number(c.dataset.i)]);
}

/* ── расчёт ──────────────────────────────────────────────────────────────── */
function evalNow() {
    const L = lens();
    if (S.ctx) {
        return evaluate(S.ctx.flat.concat(S.flat), {
            dayLens: S.ctx.lens.concat(L), contextDays: S.ctx.days, exceptions: S.exc, shortBreaks: S.breaks,
        });
    }
    return evaluate(S.flat, { dayLens: L, exceptions: S.exc, shortBreaks: S.breaks });
}

function slotLabel(idx) {
    const L = lens(), st = dayStarts(L);
    let d = 0;
    while (d < S.days - 1 && st[d + 1] <= idx) d++;
    if (idx < 0) {
        const pd = dateOf(shiftYm(S.ym, -1), daysIn(shiftYm(S.ym, -1)) - 1);
        return pd.toLocaleDateString(LOC, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    return `${dateOf(S.ym, d).getUTCDate()} ${hhmm(idx - st[d])}`;
}

function recalc() {
    const r = S.result = evalNow();
    const L = lens(), st = dayStarts(L);

    /* строки: итог суток и отметки */
    const violSlots = new Set();
    for (const v of r.violations) {
        if ((v.rule === 'rest24' || v.rule === 'rest7d') && v.at >= 0) {
            const win = v.rule === 'rest24' ? SLOTS_PER_DAY : SLOTS_PER_DAY * 7;
            for (let k = Math.max(0, v.at); k < Math.min(S.flat.length, v.at + win); k++) violSlots.add(k);
        }
    }
    $('rhGrid').querySelectorAll('.rh-row[data-day]').forEach((row, d) => {
        const p = r.perDay[d];
        const sum = row.querySelector('.rh-sum');
        sum.textContent = p ? fmtH(p.restH) : '';
        row.classList.toggle('bad', !!(p && p.bad.length));
        sum.title = p && p.bad.length ? p.bad.map(b => t('bad_' + b)).join('; ') : '';
    });
    for (const c of $('rhGrid').querySelectorAll('.rh-c[data-i]')) c.classList.toggle('v', violSlots.has(Number(c.dataset.i)));

    /* факты */
    const facts = $('rhFacts');
    facts.textContent = '';
    const add = (l, v, sub, state) => {
        const f = el('div', 'tc-stat' + (state ? ' ' + state : ''));
        f.append(el('div', 'tc-stat-l', l), el('div', 'tc-stat-v', v));
        if (sub) f.append(el('div', 'tc-stat-s', sub));
        facts.append(f);
    };
    const st2 = (have, need) => have === null ? '' : (have < need - 1e-9 ? 'bad' : (have < need + 3 ? 'warn' : 'good'));
    add(t('f_min24'), r.minRest24 === null ? '—' : fmtH(r.minRest24) + ' ' + t('unit_h'),
        t('f_min24_sub', { n: RULES.rest24 }), st2(r.minRest24, RULES.rest24));
    add(t('f_min7'), r.minRest7d === null ? '—' : fmtH(r.minRest7d) + ' ' + t('unit_h'),
        r.enoughDataFor7d ? t('f_min7_sub', { n: r.need7 }) : t('f_min7_short'),
        r.enoughDataFor7d ? st2(r.minRest7d, r.need7) : '');
    add(t('f_slack'), r.slack7 === null ? '—' : (r.slack7 >= 0 ? '+' : '−') + fmtH(Math.abs(r.slack7)) + ' ' + t('unit_h'),
        t('f_slack_sub'), r.slack7 === null ? '' : (r.slack7 < 0 ? 'bad' : r.slack7 < 8 ? 'warn' : 'good'));
    add(t('f_maxwork'), r.maxWork24 === null ? '—' : fmtH(r.maxWork24) + ' / ' + fmtH(r.maxWork7d ?? 0),
        t('f_maxwork_sub', { a: RULES.work24, b: RULES.work7d }), r.compliantWork ? 'good' : 'bad');
    $('rhSumSub').textContent = S.exc ? t('sum_sub_exc') : '';

    /* нарушения — каждое эпизодом, а не одно «худшее окно» */
    const box = $('rhViolations');
    box.textContent = '';
    if (!r.violations.length) {
        box.append(el('p', 'rh-ok', t('v_none')));
    } else {
        const ul = el('ul', 'rh-viol');
        const byRule = {};
        for (const v of r.violations) (byRule[v.rule] ||= []).push(v);
        for (const [rule, list] of Object.entries(byRule)) {
            if (rule === 'rest24' || rule === 'rest7d') {
                list.slice(0, 4).forEach(v => {
                    const li = el('li');
                    li.append(el('b', null, t('v_' + rule)),
                        el('span', null, t('v_window', { have: fmtH(v.have), need: v.need, from: slotLabel(v.at) })));
                    ul.append(li);
                });
                if (list.length > 4) ul.append(el('li', null, t('v_more', { n: list.length - 4 })));
            } else {
                const li = el('li');
                li.append(el('b', null, t('v_' + rule)));
                const ds = list.filter(v => v.day != null).map(v => dateOf(S.ym, v.day).getUTCDate());
                if (ds.length) li.append(el('span', null, t('v_days', { list: ds.join(', ') })));
                ul.append(li);
            }
        }
        box.append(ul);
    }

    const notes = [t('totals', { work: fmtH(r.totalWork), rest: fmtH(r.totalRest), days: r.days })];
    if (S.ctx) notes.push(t('ctx_note', { n: S.ctx.days }));
    if (S.breaks && r.shortBreaks) notes.push(t('breaks_note', { n: r.shortBreaks }));
    $('rhTotals').textContent = notes.join(' ');
    if (S.editDay != null && $('rhDay').open) renderDayBars();
}

/* ── диалог суток ────────────────────────────────────────────────────────── */
function openDay(d) {
    S.editDay = Math.max(0, Math.min(S.days - 1, d));
    const dlg = $('rhDay');
    renderDayBars();
    if (!dlg.open) { if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', ''); }
    funnel('day_editor', {});
}

function renderDayBars() {
    const d = S.editDay, L = lens(), st = dayStarts(L);
    const dt = dateOf(S.ym, d);
    $('rhDayT').textContent = dt.toLocaleDateString(LOC, { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' });
    $('rhClock').value = String(S.clock[d] || 0);
    const host = $('rhDayBars');
    host.textContent = '';
    const len = L[d];
    for (let half = 0; half < 2; half++) {
        const scale = el('div', 'rh-halfscale');
        for (let h = half * 12; h < half * 12 + 12; h += 1) scale.append(el('span', null, String(h).padStart(2, '0')));
        const bar = el('div', 'rh-half');
        for (let s = half * 24; s < half * 24 + 24; s++) {
            const c = el('span', 'rh-c' + (s % 2 === 1 ? ' h' : '') + (s < len ? '' : ' x'));
            if (s < len) { c.dataset.i = String(st[d] + s); c.classList.toggle('w', !!S.flat[st[d] + s]); }
            bar.append(c);
        }
        host.append(scale, bar);
    }
    if (len > 48) {
        const extra = el('div', 'rh-half rh-extra-row');
        for (let s = 48; s < len; s++) {
            const c = el('span', 'rh-c');
            c.dataset.i = String(st[d] + s);
            c.classList.toggle('w', !!S.flat[st[d] + s]);
            extra.append(c);
        }
        host.append(el('div', 'rh-halfscale', t('clock_badge')), extra);
    }
    const p = S.result?.perDay[d];
    const spans = dayWorkSpans(S.flat, d, L);
    const lastWork = (() => { for (let k = st[d] + len - 1; k >= st[d]; k--) if (S.flat[k]) return k + 1; return null; })();
    let budget = null;
    if (lastWork !== null && lastWork < S.flat.length) budget = workBudget(S.ctx ? S.ctx.flat.concat(S.flat) : S.flat, lastWork + (S.ctx ? S.ctx.flat.length : 0), { exceptions: S.exc });
    const sum = $('rhDaySum');
    sum.textContent = '';
    const b = el('b', null, spans.length ? spans.map(s => s.join('–')).join(', ') : t('no_work'));
    sum.append(b, document.createTextNode(' · ' + t('day_sum', { rest: p ? fmtH(p.restH) : '—', periods: p ? p.periods : '—' })));
    if (budget !== null) sum.append(document.createTextNode(' · ' + t('day_budget', { h: fmtH(budget), from: hhmm(lastWork - st[d]) })));
    if (p && p.bad.length) sum.append(el('span', 'tc-note', ' ' + p.bad.map(x => t('bad_' + x)).join('; ')));
}

function wireDay() {
    const host = $('rhDayBars');
    let paint = null;
    const idxAt = (x, y) => { const e2 = document.elementFromPoint(x, y); return e2 && e2.dataset && e2.dataset.i ? Number(e2.dataset.i) : null; };
    host.addEventListener('pointerdown', e => {
        const c = e.target.closest('.rh-c');
        if (!c || !c.dataset.i) return;
        e.preventDefault();
        const i = Number(c.dataset.i);
        paint = !S.flat[i];
        S.flat[i] = paint; c.classList.toggle('w', paint);
        host.setPointerCapture?.(e.pointerId);
    });
    host.addEventListener('pointermove', e => {
        if (paint === null) return;
        const i = idxAt(e.clientX, e.clientY);
        if (i === null || S.flat[i] === paint) return;
        S.flat[i] = paint;
        const c = host.querySelector(`.rh-c[data-i="${i}"]`);
        if (c) c.classList.toggle('w', paint);
    });
    const end = () => { if (paint !== null) { paint = null; paintAll(); recalc(); saveMonth(); } };
    host.addEventListener('pointerup', end);
    host.addEventListener('pointercancel', end);
    $('rhDayPrev').addEventListener('click', () => openDay(S.editDay - 1));
    $('rhDayNext').addEventListener('click', () => openDay(S.editDay + 1));
    $('rhDayClose').addEventListener('click', () => { const d = $('rhDay'); d.close ? d.close() : d.removeAttribute('open'); });
    $('rhDayClear').addEventListener('click', () => {
        const L = lens(), st = dayStarts(L);
        for (let s = 0; s < L[S.editDay]; s++) S.flat[st[S.editDay] + s] = false;
        paintAll(); recalc(); saveMonth();
    });
    $('rhDayYesterday').addEventListener('click', () => {
        if (S.editDay === 0) return;
        copyDay(S.flat, S.editDay - 1, S.editDay, S.editDay, lens());
        paintAll(); recalc(); saveMonth();
    });
    $('rhClock').addEventListener('change', () => {
        setClock(S.editDay, Number($('rhClock').value));
    });
}

/** Перевод часов: сутки становятся 23/25 ч. Разметка ПО ВРЕМЕНИ суток
    сохраняется, лишние получасы добавляются в конец суток (или снимаются). */
function setClock(d, delta) {
    const oldL = lens(), oldSt = dayStarts(oldL);
    const rows = Array.from({ length: S.days }, (_, k) => S.flat.slice(oldSt[k], oldSt[k + 1]));
    if (delta) S.clock[d] = delta; else delete S.clock[d];
    const newL = lens();
    rows[d] = Array.from({ length: newL[d] }, (_, s) => s < rows[d].length ? rows[d][s] : !!rows[d][rows[d].length - 1]);
    S.flat = [].concat(...rows);
    funnel('clock_change', { delta });
    buildGrid(); recalc(); saveMonth();
}

/* ── экипаж ──────────────────────────────────────────────────────────────── */
function renderWho() {
    const sel = $('rhWho');
    sel.textContent = '';
    S.crew.forEach((p, i) => {
        const o = el('option', null, p.name || (i === 0 ? t('crew_default') : t('crew_new', { n: i + 1 })));
        o.value = p.id;
        sel.append(o);
    });
    sel.value = S.who;
    const p = S.crew.find(x => x.id === S.who) || S.crew[0];
    $('rhName').value = p?.name || '';
    $('rhRank').value = p?.rank || '';
    $('rhWatch').checked = p?.watch !== false;
    $('rhShip').value = S.ship.name || '';
    $('rhFlag').value = S.ship.flag || '';
    $('rhImo').value = S.ship.imo || '';
    $('rhWhoDel').hidden = S.crew.length < 2;
    $('rhShipSub').textContent = [p?.name, S.ship.name].filter(Boolean).join(' · ');
}

function upsell(what) {
    const n = $('rhProNote');
    n.hidden = false;
    n.textContent = '';
    n.append(document.createTextNode(t('crew_locked') + ' '));
    const a = el('a', 'tc-perk', t('pro_link'));
    a.href = B.cfg.proUrl;
    n.append(a);
    window.__toast?.(t('crew_locked'), 'info');
    funnel('perk_upsell', { what });
}

/* ── PDF и печать ────────────────────────────────────────────────────────── */
function sheetFor(who) {
    const person = S.crew.find(x => x.id === who) || {};
    const m = who === S.who ? { days: S.days, clock: S.clock, flat: S.flat } : loadMonth(who, S.ym);
    if (!m.flat) return null;
    const L = lensOf(m.days, m.clock);
    const r = evaluate(m.flat, { dayLens: L, exceptions: S.exc, shortBreaks: S.breaks });
    const st = dayStarts(L);

    const sh = el('div', 'rh-sheet');
    sh.append(el('h2', null, t('sheet_title')), el('p', 'en', t('sheet_en')));
    const hd = el('table', 'hd');
    const tr = el('tr');
    const monthName = dateOf(S.ym, 0).toLocaleDateString(LOC, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    [['sheet_ship', S.ship.name], ['sheet_flag', S.ship.flag], ['sheet_imo', S.ship.imo], ['sheet_name', person.name],
        ['sheet_rank', person.rank], ['sheet_watch', person.watch === false ? t('sheet_no') : t('sheet_yes')], ['sheet_month', monthName]]
        .forEach(([k, v]) => { const td = el('td'); td.append(el('b', null, t(k)), document.createTextNode(v || ' ')); tr.append(td); });
    hd.append(tr);
    sh.append(hd);

    /* Сетка — один холст: 31×50 ячеек в DOM html2canvas перерисовывал бы
       по элементу, а холст снимается одной картинкой. */
    const W = 1060, rowH = 15, headH = 20, dayW = 74, sumW = 160;
    const cv = document.createElement('canvas');
    const H = headH + rowH * m.days;
    cv.width = W * 2; cv.height = H * 2;
    const g = cv.getContext('2d');
    g.scale(2, 2);
    g.fillStyle = '#fff'; g.fillRect(0, 0, W, H);
    g.font = '11px Arial'; g.fillStyle = '#000'; g.textBaseline = 'middle';
    const gridW = W - dayW - sumW, cw = gridW / COLS;
    for (let h = 0; h <= 24; h += 2) g.fillText(String(h % 24).padStart(2, '0'), dayW + h * 2 * cw + 1, headH / 2);
    g.font = '10px Arial';
    g.fillText(t('col_rest24'), dayW + gridW + 6, headH / 2);
    g.fillText(t('col_rest7'), dayW + gridW + 86, headH / 2);
    g.font = '11px Arial';
    const pre = [0];
    for (let i = 0; i < m.flat.length; i++) pre.push(pre[i] + (m.flat[i] ? 0 : 1));
    for (let d = 0; d < m.days; d++) {
        const y = headH + d * rowH;
        const dt = dateOf(S.ym, d);
        g.fillStyle = '#000';
        g.fillText(dt.getUTCDate() + ' ' + dt.toLocaleDateString(LOC, { weekday: 'short', timeZone: 'UTC' }), 4, y + rowH / 2);
        for (let s = 0; s < COLS; s++) {
            const x = dayW + s * cw;
            if (s >= L[d]) { g.fillStyle = '#e8e8e8'; g.fillRect(x, y + 1, cw, rowH - 2); continue; }
            g.fillStyle = m.flat[st[d] + s] ? '#1f3f99' : '#ffffff';
            g.fillRect(x, y + 1, cw, rowH - 2);
        }
        g.strokeStyle = '#bbb'; g.lineWidth = 0.5;
        g.strokeRect(dayW, y + 1, gridW, rowH - 2);
        for (let s = 2; s < COLS; s += 2) { g.beginPath(); g.moveTo(dayW + s * cw, y + 1); g.lineTo(dayW + s * cw, y + rowH - 1); g.strokeStyle = s % 8 === 0 ? '#888' : '#ccc'; g.stroke(); }
        const p = r.perDay[d];
        g.fillStyle = p && p.bad.length ? '#b41f1f' : '#000';
        g.fillText(p ? fmtH(p.restH) : '', dayW + gridW + 10, y + rowH / 2);
        const endSlot = st[d + 1];
        const from7 = Math.max(0, endSlot - SLOTS_PER_DAY * 7);
        const r7 = (pre[endSlot] - pre[from7]) * SLOT_H;
        g.fillStyle = endSlot - from7 >= SLOTS_PER_DAY * 7 && r7 < r.need7 ? '#b41f1f' : '#000';
        g.fillText(endSlot - from7 >= SLOTS_PER_DAY * 7 ? fmtH(r7) : '—', dayW + gridW + 90, y + rowH / 2);
    }
    sh.append(cv);

    const notes = el('div', 'notes');
    notes.append(el('b', null, t('sheet_notes') + ' '));
    const list = r.violations.map(v => v.day != null
        ? t('v_' + v.rule) + ' (' + dateOf(S.ym, v.day).getUTCDate() + ')'
        : t('v_' + v.rule) + (v.at >= 0 ? ' (' + slotLabel(v.at) + ')' : ''));
    notes.append(document.createTextNode(list.length ? [...new Set(list)].join('; ') : t('sheet_none')));
    sh.append(notes);
    const sign = el('div', 'sign');
    sign.append(el('div', null, t('sheet_sign_seafarer')), el('div', null, t('sheet_sign_master')), el('div', null, t('sheet_date')));
    sh.append(sign);
    return sh;
}

function buildPrint(allCrew) {
    const host = $('rhPrintHost');
    host.textContent = '';
    const ids = allCrew ? S.crew.map(p => p.id) : [S.who];
    for (const id of ids) { const s = sheetFor(id); if (s) host.append(s); }
    return host;
}

async function makePdf() {
    const all = isPro() && S.crew.length > 1;
    /* Лист отдаётся примитиву НЕприкреплённым: прикреплённый узел с position:fixed
       схлопывает контейнер html2pdf до нулевой высоты, и холст выходит пустым. */
    const host = el('div');
    for (const id of all ? S.crew.map(p => p.id) : [S.who]) { const sh = sheetFor(id); if (sh) host.append(sh); }
    funnel('pdf', { crew: all ? S.crew.length : 1 });
    try {
        const res = await window.__generatePDF?.({
            element: host, asIs: true, orientation: 'landscape',
            filename: `rest-hours-${S.ym}.pdf`,
            strings: { genError: t('pdf_error'), libError: t('pdf_error') },
        });
        if (res && res.ok === false) window.__toast?.(t('pdf_error'), 'error');
    } catch {
        window.__toast?.(t('pdf_error'), 'error');
    }
}

/* ── CSV (Премиум) и резервная копия ─────────────────────────────────────── */
function exportCsv() {
    if (!isPro()) { upsell('csv'); return; }
    const rows = [];
    const ids = S.crew.map(p => p.id);
    for (const id of ids) {
        const person = S.crew.find(x => x.id === id);
        const m = id === S.who ? { days: S.days, clock: S.clock, flat: S.flat } : loadMonth(id, S.ym);
        if (!m.flat) continue;
        const L = lensOf(m.days, m.clock), st = dayStarts(L);
        const r = evaluate(m.flat, { dayLens: L, exceptions: S.exc, shortBreaks: S.breaks });
        for (let d = 0; d < m.days; d++) {
            const cells = [];
            for (let s = 0; s < L[d]; s++) cells.push(m.flat[st[d] + s] ? 'W' : '');
            rows.push([person.name, person.rank, dateOf(S.ym, d).toISOString().slice(0, 10), r.perDay[d].restH,
                dayWorkSpans(m.flat, d, L).map(x => x.join('-')).join(' '), r.perDay[d].bad.join(' '), ...cells]);
        }
    }
    const head = ['name', 'rank', 'date', 'rest_hours', 'work_periods', 'flags', ...Array.from({ length: 50 }, (_, i) => hhmm(i))];
    const esc = v => { const s = String(v ?? ''); return /[",;\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const csv = '\uFEFF' + [head, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
    window.__downloadFile?.(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `rest-hours-${S.ym}.csv`);
    funnel('csv', { crew: ids.length });
}

function exportJson() {
    const months = {};
    for (const p of S.crew) {
        for (let k = -12; k <= 1; k++) {
            const ym = shiftYm(S.ym, k);
            const v = store.load(monthKey(p.id, ym), null);
            if (v) months[`${p.id}:${ym}`] = v;
        }
    }
    const data = { format: 'timbrica-rest-hours-2', crew: S.crew, ship: S.ship, months, exported: new Date().toISOString() };
    window.__downloadFile?.(new Blob([JSON.stringify(data, null, 1)], { type: 'application/json' }), `rest-hours-backup-${S.ym}.json`);
    funnel('export_json', {});
}

function importJson() {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.json,application/json';
    inp.addEventListener('change', async () => {
        const f = inp.files && inp.files[0];
        if (!f) return;
        try {
            const data = JSON.parse(await f.text());
            if (data.format === 'timbrica-rest-hours-2' && data.months) {
                S.crew = Array.isArray(data.crew) && data.crew.length ? data.crew : S.crew;
                S.ship = data.ship || S.ship;
                for (const [k, v] of Object.entries(data.months)) {
                    const [id, ym] = k.split(':');
                    if (/^\d{4}-\d{2}$/.test(ym) && v && v.flat) store.save(monthKey(id, ym), v);
                }
                S.who = S.crew[0].id;
            } else if (data.format === 'timbrica-rest-hours-1' && Array.isArray(data.rows)) {
                /* v1 хранил периоды словами — восстанавливаем по ним. */
                const ym = String(data.start).slice(0, 7);
                const days = daysIn(ym);
                const flat = new Array(days * SLOTS_PER_DAY).fill(false);
                data.rows.forEach((row, d) => (row.work || []).forEach(span => {
                    const [a, b] = String(span).split('–');
                    const ia = toSlot(a), ib = b === '00:00' ? SLOTS_PER_DAY : toSlot(b);
                    for (let s = ia; s < ib && d < days; s++) flat[d * SLOTS_PER_DAY + s] = true;
                }));
                store.save(monthKey(S.who, ym), { flat: packBits(flat), clock: {}, at: Date.now() });
                const p = S.crew.find(x => x.id === S.who);
                if (p && data.seafarer) { p.name = data.seafarer; p.rank = data.rank || p.rank; }
                if (data.ship) S.ship.name = data.ship;
                S.ym = ym;
            } else throw new Error('format');
            saveMeta(); renderWho(); openMonth(S.ym);
            window.__toast?.(t('import_ok'), 'success');
            funnel('import_json', {});
        } catch {
            window.__toast?.(t('import_bad'), 'error');
        }
    });
    inp.click();
}
const toSlot = s => { const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim()); return m ? Number(m[1]) * 2 + (Number(m[2]) >= 30 ? 1 : 0) : 0; };

/* ── подключение ─────────────────────────────────────────────────────────── */
function bind() {
    $('rhMonth').addEventListener('change', e => { if (/^\d{4}-\d{2}$/.test(e.target.value)) { store.save('ym', e.target.value); openMonth(e.target.value); } });
    $('rhPrev').addEventListener('click', () => openMonth(shiftYm(S.ym, -1)));
    $('rhNext').addEventListener('click', () => openMonth(shiftYm(S.ym, 1)));
    $('rhPattern').addEventListener('change', () => {
        const copy = $('rhPattern').value === 'copy';
        $('rhSrcBox').hidden = !copy;
        $('rhSrcBox').parentElement.classList.toggle('copy', copy);
    });
    $('rhApply').addEventListener('click', () => {
        const p = $('rhPattern').value;
        const from = Math.max(1, Number($('rhFrom').value) || 1) - 1;
        const to = Math.min(S.days, Number($('rhTo').value) || S.days) - 1;
        if (to < from) { window.__toast?.(t('range_bad'), 'warning'); return; }
        if (p === 'copy') copyDay(S.flat, Math.max(1, Math.min(S.days, Number($('rhSrc').value) || 1)) - 1, from, to, lens());
        else applyPattern(S.flat, p, from, to, lens());
        paintAll(); recalc(); saveMonth();
        funnel('pattern', { p, days: to - from + 1 });
    });
    $('rhExc').addEventListener('change', e => { S.exc = e.target.checked; saveMeta(); recalc(); funnel('opt_exceptions', { on: S.exc ? 1 : 0 }); });
    $('rhBreaks').addEventListener('change', e => { S.breaks = e.target.checked; saveMeta(); recalc(); });
    $('rhWho').addEventListener('change', e => { S.who = e.target.value; saveMeta(); renderWho(); openMonth(S.ym); });
    $('rhWhoAdd').addEventListener('click', () => {
        if (!isPro() && S.crew.length >= 1) { upsell('crew'); return; }
        const id = 'p' + Date.now().toString(36);
        S.crew.push({ id, name: '', rank: '', watch: true });
        S.who = id;
        saveMeta(); renderWho(); openMonth(S.ym);
        $('rhShipDrawer').open = true;
        $('rhName').focus();
        funnel('crew_add', { n: S.crew.length });
    });
    $('rhWhoDel').addEventListener('click', () => {
        if (S.crew.length < 2 || !window.confirm(t('crew_remove_confirm'))) return;
        S.crew = S.crew.filter(p => p.id !== S.who);
        S.who = S.crew[0].id;
        saveMeta(); renderWho(); openMonth(S.ym);
    });
    const person = () => S.crew.find(x => x.id === S.who);
    $('rhName').addEventListener('input', e => { const p = person(); if (p) p.name = e.target.value; saveMeta(); const o = $('rhWho').selectedOptions[0]; if (o) o.textContent = e.target.value || t('crew_default'); });
    $('rhRank').addEventListener('input', e => { const p = person(); if (p) p.rank = e.target.value; saveMeta(); });
    $('rhWatch').addEventListener('change', e => { const p = person(); if (p) p.watch = e.target.checked; saveMeta(); });
    $('rhShip').addEventListener('input', e => { S.ship.name = e.target.value; saveMeta(); });
    $('rhFlag').addEventListener('input', e => { S.ship.flag = e.target.value; saveMeta(); });
    $('rhImo').addEventListener('input', e => { S.ship.imo = e.target.value.replace(/\D/g, '').slice(0, 7); e.target.value = S.ship.imo; saveMeta(); });
    $('rhPdf').addEventListener('click', makePdf);
    $('rhPrint').addEventListener('click', () => { buildPrint(isPro() && S.crew.length > 1); funnel('print', {}); window.print(); });
    $('rhCsv').addEventListener('click', exportCsv);
    $('rhExport').addEventListener('click', exportJson);
    $('rhImport').addEventListener('click', importJson);
    wireGrid();
    wireDay();
}

function boot() {
    try {
        migrate();
        S.crew = store.load('crew', S.crew) || S.crew;
        if (!S.crew.length) S.crew = [{ id: 'p1', name: '', rank: '', watch: true }];
        S.ship = Object.assign({ name: '', flag: '', imo: '' }, store.load('ship', {}) || {});
        S.who = store.load('who', null) || S.crew[0].id;
        if (!S.crew.some(p => p.id === S.who)) S.who = S.crew[0].id;
        const o = store.load('opts', {}) || {};
        S.exc = !!o.exc; S.breaks = !!o.breaks;
        $('rhExc').checked = S.exc; $('rhBreaks').checked = S.breaks;
        bind();
        renderWho();
        openMonth(ymOf(new Date()));
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'rest-hours');
        const b = $('rhFacts');
        if (b) b.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
